export const PORTFOLIO_PLATFORM_SIGN_UP_ORIGIN = 'https://app.extern.com';
export const PORTFOLIO_SIGN_UP_PATH = '/sign-up';
export const PORTFOLIO_SIGN_UP_UTM_SOURCE = 'portfolio';
export const PORTFOLIO_SIGN_UP_UTM_MEDIUM = 'referral';
export const PORTFOLIO_SIGN_UP_FOOTER_CONTENT = 'build_yours_footer';

/**
 * `utm_content` when the claim flow could not say which arm produced the draft.
 *
 * Lives here rather than in `apps/portfolio` because the platform reads these values back at
 * sign-up and cannot import from another app. One list, so the writer and the reader cannot drift.
 */
export const PORTFOLIO_CLAIM_SIGN_UP_CONTENT = 'portfolio_new_claim';

/** `utm_content` per claim arm, so a signup traces to the page that produced it. */
export const PORTFOLIO_CLAIM_SIGN_UP_CONTENT_BY_ARM = {
  claim_link: 'portfolio_claim_link',
  resume_drop: 'portfolio_resume_drop',
} as const;

export type PortfolioClaimArm = keyof typeof PORTFOLIO_CLAIM_SIGN_UP_CONTENT_BY_ARM;

/**
 * `utm_content` per arm for a sign-up offered BEFORE the page is claimed. The anonymous editor shows
 * "Create account" from the first edit, and that account adopts nothing: an unclaimed draft carries no
 * email for adoption to match. Kept out of {@link isPortfolioClaimSignUpContent} on purpose — the
 * platform must still walk that account through turn-on, setup and the walkthrough. Attribution to
 * the arm survives; only the "arrived with a page" claim does not.
 */
export const PORTFOLIO_DRAFT_SIGN_UP_CONTENT_BY_ARM = {
  claim_link: 'portfolio_claim_link_draft',
  resume_drop: 'portfolio_resume_drop_draft',
} as const satisfies Record<PortfolioClaimArm, string>;

const CLAIM_SIGN_UP_CONTENTS: readonly string[] = [
  PORTFOLIO_CLAIM_SIGN_UP_CONTENT,
  ...Object.values(PORTFOLIO_CLAIM_SIGN_UP_CONTENT_BY_ARM),
];

/**
 * True when a sign-up arrived from a page the visitor built and claimed before they had an account.
 *
 * {@link PORTFOLIO_SIGN_UP_FOOTER_CONTENT} is deliberately not one of these: clicking "Build yours"
 * on a stranger's portfolio starts a page from nothing, and adopts no work.
 */
export function isPortfolioClaimSignUpContent(utmContent: string | null | undefined): boolean {
  return utmContent != null && CLAIM_SIGN_UP_CONTENTS.includes(utmContent);
}

export interface PortfolioSignUpAttribution {
  portfolioCode?: string;
  publicUrl: string;
}

export function buildPortfolioSignUpUrl(
  portfolio: PortfolioSignUpAttribution,
  options?: {
    utmContent?: string;
    origin?: string;
  },
): string {
  const origin = options?.origin ?? PORTFOLIO_PLATFORM_SIGN_UP_ORIGIN;
  const url = new URL(PORTFOLIO_SIGN_UP_PATH, origin);

  url.searchParams.set('utm_source', PORTFOLIO_SIGN_UP_UTM_SOURCE);
  url.searchParams.set('utm_medium', PORTFOLIO_SIGN_UP_UTM_MEDIUM);
  url.searchParams.set('utm_content', options?.utmContent ?? PORTFOLIO_SIGN_UP_FOOTER_CONTENT);
  url.searchParams.set('portfolio_url', portfolio.publicUrl);

  if (portfolio.portfolioCode) {
    url.searchParams.set('utm_campaign', portfolio.portfolioCode);
    url.searchParams.set('portfolio_code', portfolio.portfolioCode);
  }

  return url.toString();
}
